/* eslint-disable no-restricted-syntax */
import type {
  GridNode,
  GridRow,
  GridCollection as IGridCollection,
} from '@react-types/grid';
import type {Key} from 'react';

interface GridCollectionOptions<T> {
  columnCount: number;
  items: GridRow<T>[];
  visitNode?: (cell: GridNode<T>) => GridNode<T>;
}

export class GridCollection<T> implements IGridCollection<T> {
  keyMap: Map<Key, GridNode<T>> = new Map();

  columnCount: number;

  rows: GridNode<T>[];

  constructor(opts?: GridCollectionOptions<T>) {
    this.keyMap = new Map();
    this.columnCount = opts?.columnCount ?? 0;
    this.rows = [];

    const visit = (node: GridNode<T>) => {
      // If the node is the same object as the previous node for the same key,
      // we can skip this node and its children. We always visit columns though,
      // because we depend on order to build the columns array.
      const prevNode = this.keyMap.get(node.key);
      if (opts?.visitNode) {
        // eslint-disable-next-line no-param-reassign
        node = opts.visitNode(node);
      }

      this.keyMap.set(node.key, node);

      const childKeys = new Set();
      let last: GridNode<T> | undefined;
      for (const child of node.childNodes) {
        if (child.type === 'cell' && child.parentKey == null) {
          // if child is a cell parent key isn't already established by the collection, match child node to parent row
          child.parentKey = node.key;
        }
        childKeys.add(child.key);

        if (last) {
          last.nextKey = child.key;
          child.prevKey = last.key;
        } else {
          child.prevKey = null;
        }

        visit(child);
        last = child;
      }

      if (last) {
        last.nextKey = null;
      }

      // Remove deleted nodes and their children from the key map
      if (prevNode) {
        for (const child of prevNode.childNodes) {
          if (!childKeys.has(child.key)) {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            remove(child);
          }
        }
      }
    };

    let remove = (node: GridNode<T>) => {
      this.keyMap.delete(node.key);
      for (const child of node.childNodes) {
        if (this.keyMap.get(child.key) === child) {
          remove(child);
        }
      }
    };

    let last: GridNode<T> | undefined;
    opts?.items.forEach((node, i) => {
      const rowNode = {
        level: 0,
        key: `row-${i}`,
        // @ts-expect-error - 'type' is specified more than once, so this usage will be overwritten.
        type: 'row',
        value: undefined,
        hasChildNodes: true,
        // @ts-expect-error - 'childNodes' is specified more than once, so this usage will be overwritten.
        childNodes: [...node.childNodes],
        rendered: undefined,
        textValue: undefined,
        ...node,
      } as unknown as GridNode<T>;

      if (last) {
        last.nextKey = rowNode.key;
        rowNode.prevKey = last.key;
      } else {
        rowNode.prevKey = null;
      }

      this.rows.push(rowNode);
      visit(rowNode);

      last = rowNode;
    });

    if (last) {
      last.nextKey = null;
    }
  }

  *[Symbol.iterator]() {
    yield* [...this.rows];
  }

  get size() {
    return [...this.rows].length;
  }

  getKeys() {
    return this.keyMap.keys();
  }

  getKeyBefore(key: Key) {
    const node = this.keyMap.get(key);
    return node ? node.prevKey! : null;
  }

  getKeyAfter(key: Key) {
    const node = this.keyMap.get(key);
    return node ? node.nextKey! : null;
  }

  getFirstKey() {
    return [...this.rows][0]?.key;
  }

  getLastKey() {
    const rows = [...this.rows];
    return rows[rows.length - 1]?.key;
  }

  getItem(key: Key) {
    return this.keyMap.get(key) ?? null;
  }

  at(idx: number) {
    const keys = [...this.getKeys()];
    return this.getItem(keys[idx]);
  }

  getChildren(key: Key): Iterable<GridNode<T>> {
    const node = this.keyMap.get(key);
    return node?.childNodes || [];
  }
}
